import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAddonTransactions } from '@/hooks/addons';
import { LoadingState } from '@/components/LoadingState';
import { useState } from 'react';
import { History, Coins, HardDrive, Calendar, DollarSign, RefreshCw, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import type { AddonTransaction } from '@shared/schema';

interface TransactionHistoryProps {
  className?: string;
  compact?: boolean;
  'data-testid'?: string;
}

export function TransactionHistory({ 
  className = '', 
  compact = false,
  'data-testid': testId 
}: TransactionHistoryProps) {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  const { 
    data: transactionsResponse, 
    isLoading, 
    refetch 
  } = useAddonTransactions({
    page,
    limit: compact ? 5 : 10,
    type: typeFilter === 'all' ? undefined : typeFilter
  });

  if (isLoading) {
    return (
      <Card className={className} data-testid={testId}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Transaction History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingState message="Loading transactions..." />
        </CardContent>
      </Card>
    );
  }

  const transactions = transactionsResponse?.transactions || [];
  const pagination = transactionsResponse?.pagination;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'refunded':
        return <RotateCcw className="h-4 w-4 text-orange-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'default';
      case 'refunded':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getTransactionIcon = (transaction: AddonTransaction) => {
    // Note: We'd need to join with addon catalog to get the type
    // For now, we'll infer from units granted or use a generic icon
    if (transaction.unitsGranted > 100) {
      return <Coins className="h-4 w-4 text-purple-600" />;
    }
    return <HardDrive className="h-4 w-4 text-teal-600" />;
  };

  return (
    <Card className={`transition-all duration-200 hover:shadow-md ${className}`} data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Transaction History
          </div>
          {!compact && (
            <div className="flex items-center gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-32" data-testid={`${testId}-type-filter`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="illustrations">Illustrations</SelectItem>
                  <SelectItem value="storage">Storage</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                data-testid={`${testId}-refresh-button`}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid={`${testId}-empty-state`}>
            <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No transactions found</p>
            <p className="text-sm">Your add-on purchases will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="border rounded-lg p-4 transition-colors hover:bg-muted/50"
                data-testid={`${testId}-transaction-${transaction.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    {getTransactionIcon(transaction)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm" data-testid={`${testId}-transaction-${transaction.id}-units`}>
                          {transaction.unitsGranted.toLocaleString()} units
                        </span>
                        <Badge 
                          variant={getStatusColor(transaction.status)}
                          className="flex items-center gap-1"
                          data-testid={`${testId}-transaction-${transaction.id}-status`}
                        >
                          {getStatusIcon(transaction.status)}
                          {transaction.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(transaction.createdAt), 'MMM dd, yyyy')}
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          ${parseFloat(transaction.amountPaid.toString()).toFixed(2)} {transaction.currency.toUpperCase()}
                        </div>
                        {transaction.quantity > 1 && (
                          <span>Qty: {transaction.quantity}</span>
                        )}
                      </div>
                      {transaction.source !== 'stripe' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Source: {transaction.source}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div 
                      className="font-semibold text-sm"
                      data-testid={`${testId}-transaction-${transaction.id}-amount`}
                    >
                      ${parseFloat(transaction.amountPaid.toString()).toFixed(2)}
                    </div>
                    {!compact && (
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(transaction.createdAt), 'HH:mm')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!compact && pagination && pagination.totalPages > 1 && (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
                {pagination.totalCount && ` (${pagination.totalCount} total)`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  data-testid={`${testId}-prev-page`}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                  data-testid={`${testId}-next-page`}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}